import { ApolloProvider } from '@apollo/client';
import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { clientViewService } from '@/helpers/clientViewService';
import { useAuth0 } from '@auth0/auth0-react';
import { getAccessToken } from '@/services/auth/tokenService';
import React, { useEffect, useState } from 'react';

const GRAPHQL_URL = `${import.meta.env.VITE_SCP_API_URL}/graphql`;
const AuthorizedApolloProvider = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated } = useAuth0();
    const [token, setToken] = useState<any>();

    useEffect(() => {
        if (!isAuthenticated) return;

        getAccessToken()
            .then((response) => {
                if (response) {
                    setToken(response);
                }
            })
            .catch((error) => {
                // Unrecoverable failures are handled by the token service, which signs the
                // user out. Anything else is logged instead of becoming an unhandled rejection.
                console.error('[Auth] Failed to obtain an access token for GraphQL:', error);
            });
    }, [isAuthenticated]);

    const headersMiddleware = new ApolloLink((operation, forward) => {
        operation.setContext(({ headers = {} }) => {
            return {
                headers: {
                    ...headers,
                    authorization: token ? `bearer ${token}` : '',
                },
            };
        });

        return forward(operation);
    });

    const httpLink = createHttpLink({
        uri: GRAPHQL_URL,
    });

    const dynamicUrlLink = new ApolloLink((operation, forward) => {
        const clientId = clientViewService.getSelectedClientId();
        const isViewAsClient = clientViewService.isViewAsClient();

        if (clientId && isViewAsClient) {
            operation.setContext((context: any) => {
                const currentUri = context.uri || GRAPHQL_URL;

                if (/\/graphql$/.test(currentUri)) {
                    return {
                        uri: currentUri.replace('/graphql', `/clients/${clientId}/graphql`),
                    };
                }
                return {};
            });
        }

        return forward(operation);
    });

    const errorLink = onError(({ graphQLErrors, networkError, response, operation, forward }) => {
        if (graphQLErrors)
            graphQLErrors.forEach(({ message, locations, path }) =>
                console.log(
                    `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                ),
            );
        if (networkError) console.log(`[Network error]: ${networkError}`);

        return forward(operation);
    });

    const gqClient = new ApolloClient({
        link: from([errorLink, headersMiddleware, dynamicUrlLink, httpLink]),
        cache: new InMemoryCache({
            resultCaching: false,
        }),
    });

    return <ApolloProvider client={gqClient}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;
